import React from 'react';
import { ImagePlus } from 'lucide-react';
import type { OfferDraftImagePayload } from '../../../types';

function firstImageFile(files: FileList | null | undefined): File | null {
  if (!files?.length) return null;
  return Array.from(files).find((file) => file.type.startsWith('image/')) ?? null;
}

type OfferRowImageUploadProps = {
  image?: OfferDraftImagePayload | null;
  readOnly?: boolean;
  onUpload: (file: File) => void;
  /** `dropzone` = sichtbare Ablagefläche in der Kalkulation. */
  variant?: 'compact' | 'dropzone';
};

export function OfferRowImageUpload({
  image,
  readOnly = false,
  onUpload,
  variant = 'compact',
}: OfferRowImageUploadProps) {
  const [dragOver, setDragOver] = React.useState(false);
  const inputId = React.useId();

  const acceptFile = (file: File | null) => {
    if (!file || readOnly) return;
    onUpload(file);
  };

  const onDragOver = (event: React.DragEvent) => {
    if (readOnly || !event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';
    setDragOver(true);
  };

  const onDragLeave = (event: React.DragEvent) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    setDragOver(false);
  };

  const onDrop = (event: React.DragEvent) => {
    const file = firstImageFile(event.dataTransfer.files);
    if (!file) return;
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
    acceptFile(file);
  };

  const preview = image?.src ? (
    <img
      src={image.src}
      alt={image.alt || ''}
      className="max-h-20 rounded border border-app-border"
    />
  ) : null;

  const picker = readOnly ? null : (
    <label
      htmlFor={inputId}
      className="inline-flex cursor-pointer items-center gap-1 text-[9.5pt] text-blue-700"
    >
      <ImagePlus className="h-3.5 w-3.5" />
      {variant === 'dropzone' ? 'Bild hochladen oder hierher ziehen' : 'Bild hochladen'}
      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          acceptFile(event.target.files?.[0] ?? null);
          event.target.value = '';
        }}
      />
    </label>
  );

  if (variant === 'dropzone') {
    return (
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`space-y-1.5 rounded-md border border-dashed px-3 py-2 ${
          dragOver ? 'border-pipedrive-blue bg-blue-50' : 'border-app-border bg-app-surface-muted/40'
        }`}
      >
        {picker}
        {preview}
        {!preview && readOnly ? (
          <p className="text-sm text-app-text-subtle">Kein Bild</p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-1" onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      {picker}
      {preview}
    </div>
  );
}
